// src/components/RotatingPopup.jsx
import { useState, useEffect, useRef } from "react";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaRocket,
  FaBrain,
  FaCode,
} from "react-icons/fa";
import { SiUpwork } from "react-icons/si";
import { motion } from "framer-motion";

const popupContents = [
  {
    id: "subscribe",
    type: "subscribe",
    title: "Stay in the Loop",
    subtitle: "Tech insights for modern developers",
    description:
      "Subscribe to my newsletter and never miss a post on AI, full-stack development, and the future of technology.",
    buttonText: "Subscribe Now",
    buttonLink: "https://firabyte.tech",
    icon: <FaEnvelope className="h-6 w-6" />,
  },
  {
    id: "profile",
    type: "profile",
    title: "Firatol Esayas Tefera",
    subtitle: "Software Engineer | Generative AI | Full-Stack",
    description:
      "Building AI-powered web applications with RAG, LLMs, and modern full-stack technologies. Available for freelance projects and technical consulting.",
    buttonText: "Hire me on Upwork",
    buttonLink: "https://www.upwork.com/freelancers/~013702dbb39e143318",
    icon: <FaUser className="h-6 w-6" />,
    image: "/images/man.jpg",
  },
  {
    id: "ai-services",
    type: "service",
    title: "AI & Automation Services",
    subtitle: "Generative AI | RAG | Workflow Automation",
    description:
      "I build intelligent AI solutions including RAG systems, AI agents, LLM-powered applications, and automated workflows.",
    buttonText: "Hire me on Upwork",
    buttonLink: "https://www.upwork.com/freelancers/~013702dbb39e143318",
    icon: <FaBrain className="h-6 w-6" />,
  },
  {
    id: "fullstack",
    type: "service",
    title: "Full-Stack Development",
    subtitle: "Modern Web Applications",
    description:
      "End-to-end development with React, Next.js, Node.js, and PostgreSQL. Scalable, performant, and user-centered digital products.",
    buttonText: "Hire me on Upwork",
    buttonLink: "https://www.upwork.com/freelancers/~013702dbb39e143318",
    icon: <FaCode className="h-6 w-6" />,
  },
];

export function RotatingPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentContent, setCurrentContent] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState("");

  const initialTimerRef = useRef(null);
  const rotationTimerRef = useRef(null);

  useEffect(() => {
    // Check if popup has been shown before
    const shown = localStorage.getItem("popupShown");
    if (shown) {
      setHasBeenShown(true);
    }

    initialTimerRef.current = setTimeout(() => {
      setCurrentContent(popupContents[0]);
      setCurrentIndex(0);
      setIsVisible(true);
      if (!hasBeenShown) {
        localStorage.setItem("popupShown", "true");
        setHasBeenShown(true);
      }
    }, 3000);

    return () => {
      if (initialTimerRef.current) clearTimeout(initialTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !currentContent) return;

    if (rotationTimerRef.current) {
      clearInterval(rotationTimerRef.current);
    }

    rotationTimerRef.current = setInterval(() => {
      setIsClosing(true);

      setTimeout(() => {
        const nextIndex = (currentIndex + 1) % popupContents.length;
        setCurrentContent(popupContents[nextIndex]);
        setCurrentIndex(nextIndex);
        setIsClosing(false);
      }, 300);
    }, 25000);

    return () => {
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
      }
    };
  }, [isVisible, currentContent, currentIndex]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
      }
    }, 300);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubscribing(true);
    setSubscribeMessage("");

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribeMessage("✅ Subscribed successfully!");
        setEmail("");
        setTimeout(handleClose, 2000);
      } else {
        setSubscribeMessage("❌ " + (data.error || "Something went wrong"));
      }
    } catch (error) {
      setSubscribeMessage("❌ Network error. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  if (!isVisible || !currentContent) return null;

  const isMiniPopup = hasBeenShown;

  // Full Screen Popup
  if (!isMiniPopup) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="relative max-w-md w-full bg-[#e5e5e0] border border-black/10 rounded-2xl shadow-2xl p-8 transform transition-all duration-300 scale-100">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-black/10 transition-colors text-black/60 hover:text-black"
          >
            <FaTimes className="h-5 w-5" />
          </button>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-black/10 flex items-center justify-center text-black">
              {currentContent.icon}
            </div>

            <h2 className="text-2xl font-amiamie font-bold mb-2 text-black">
              {currentContent.title}
            </h2>
            <p className="text-sm text-black/70 font-medium mb-2">
              {currentContent.subtitle}
            </p>
            <p className="text-black/60 text-sm leading-relaxed mb-6">
              {currentContent.description}
            </p>

            <div className="space-y-3">
              {currentContent.id === "subscribe" ? (
                <form onSubmit={handleSubscribe} className="space-y-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-black/20 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-black/30 text-sm text-black"
                    required
                    disabled={isSubscribing}
                  />
                  <button
                    type="submit"
                    disabled={isSubscribing}
                    className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-black/80 transition-colors font-medium disabled:opacity-50"
                  >
                    {isSubscribing ? "Subscribing..." : "Subscribe"}
                  </button>
                  {subscribeMessage && (
                    <p
                      className={`text-sm ${subscribeMessage.includes("✅") ? "text-green-600" : "text-red-600"}`}
                    >
                      {subscribeMessage}
                    </p>
                  )}
                </form>
              ) : (
                <>
                  <a
                    href={currentContent.buttonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-black/80 transition-colors font-medium"
                  >
                    <SiUpwork className="h-4 w-4" />
                    {currentContent.buttonText}
                  </a>
                  <a
                    href="https://www.upwork.com/freelancers/~013702dbb39e143318"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-6 py-3 border border-black/20 rounded-lg hover:bg-black/5 transition-colors font-medium text-sm text-black"
                  >
                    <SiUpwork className="h-4 w-4" />
                    Hire me on Upwork
                  </a>
                </>
              )}
            </div>

            <button
              onClick={handleClose}
              className="mt-4 text-xs text-black/50 hover:text-black transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Mini Bottom-Right Popup
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{
        opacity: isClosing ? 0 : 1,
        scale: isClosing ? 0.9 : 1,
        y: isClosing ? 20 : 0,
      }}
      transition={{ duration: 0.3 }}
      className="fixed bottom-6 right-6 z-50 max-w-sm w-full bg-[#e5e5e0] border border-black/10 rounded-2xl shadow-2xl"
      style={{ minWidth: "280px", maxWidth: "380px" }}
    >
      <div className="p-5 relative">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-black/10 transition-colors text-black/50 hover:text-black"
        >
          <FaTimes className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-4">
          <div className="shrink-0">
            {currentContent.image ? (
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-black/20">
                <img
                  src={currentContent.image}
                  alt={currentContent.title}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-black/10 flex items-center justify-center text-black">
                {currentContent.icon}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-amiamie font-semibold text-black truncate">
              {currentContent.title}
            </h4>
            <p className="text-xs text-black/60 line-clamp-2 mt-0.5">
              {currentContent.description}
            </p>
            <div className="mt-2 flex items-center gap-2">
              {currentContent.id === "subscribe" ? (
                <a
                  href="https://firabyte.tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-black hover:underline"
                >
                  Visit Blog →
                </a>
              ) : (
                <a
                  href={currentContent.buttonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-black hover:underline"
                >
                  Hire me →
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
